import { drawingPreviewPoints } from './drawing-preview.js';
import type { Stroke } from '../types/app.js';
import type { SchemaInput } from './schema-input.js';
import { isNumber, isSchemaRecord, isString } from './runtime-values.js';

export interface DrawingLegendLink { id: string; label: string; order: number }
export type LegendSymbol = Stroke & { layerSwatch?: boolean; dataPoint?: true | import('./data-icon.js').DataIcon };
export interface DrawingLegendItem extends DrawingLegendLink { layer?: boolean; strokes: Stroke[]; symbols: LegendSymbol[] }
export const MAX_LEGEND_ITEMS = 12;
export const MAX_LEGEND_LABEL = 80;

/** Reject malformed links at every restore boundary. Old drawings need no migration. */
export function normalizeDrawingLegend(value: SchemaInput): DrawingLegendLink | undefined {
  if (!isSchemaRecord(value) || !isString(value.id) || !/^[a-z0-9_-]{1,64}$/i.test(value.id)
    || !isString(value.label) || !value.label.trim() || value.label.length > MAX_LEGEND_LABEL
    || !isNumber(value.order) || !Number.isSafeInteger(value.order) || value.order < 0 || value.order > 10000) return;
  return { id: value.id, label: value.label.trim(), order: value.order };
}

function legendStyleKey(stroke: Stroke): string {
  return JSON.stringify([stroke.tool, stroke.color, stroke.width, stroke.opacity ?? 1,
    stroke.fillColor ?? stroke.color, stroke.fillOpacity ?? 0.25, stroke.outlineOpacity ?? 1,
    stroke.lineStyle ?? 'solid', stroke.markerIcon ?? 'map-pin']);
}

export function legendSymbolKey(stroke: Stroke): string {
  const path = ['line', 'arrow', 'freehand', 'polygon'].includes(stroke.tool) ? drawingPreviewPoints(stroke) : null;
  return JSON.stringify([legendStyleKey(stroke), path]);
}

export function collectDrawingLegend(strokes: readonly Stroke[]): DrawingLegendItem[] {
  const items = new Map<string, DrawingLegendItem>();
  for (const stroke of strokes) {
    const link = normalizeDrawingLegend(stroke.legend);
    if (!link || stroke.tool === 'text') continue;
    let item = items.get(link.id);
    if (!item) { item = { ...link, strokes: [], symbols: [] }; items.set(link.id, item); }
    item.strokes.push(stroke);
    if (!item.symbols.some(symbol => legendSymbolKey(symbol) === legendSymbolKey(stroke))) item.symbols.push(stroke);
  }
  return [...items.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** Joining is explicit and requires matching styles, never merely matching colors. */
export function canJoinLegend(item: DrawingLegendItem, strokes: readonly Stroke[]): boolean {
  return strokes.length > 0 && strokes.every(stroke => item.symbols.some(symbol => legendStyleKey(symbol) === legendStyleKey(stroke)));
}

export function applyDrawingLegend(target: { legend?: DrawingLegendLink }, value: SchemaInput): void {
  const link = normalizeDrawingLegend(value);
  if (link) target.legend = link;
}
